import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LanguageSelector } from '@shared/components/common';
import { useAnalytics } from '@core/analytics';

export interface GroupHeroProps {
  /** group.groupLogoImage || group.logoImage */
  logoImage?: string;
  /** Used in the blank-headline telemetry event */
  groupSlug: string;
}

/**
 * Top-of-page hero for the group landing (Layout A — editorial hero).
 * Renders the group logo, a serif headline, and the language switcher.
 *
 * Blank headline guard (spec §4, R10): if SELECT_RESTAURANT_SCREEN_TEXT_1
 * resolves to blank or the raw key (mocked t returns key in test/CI), fall back
 * to GROUP_HERO_FALLBACK_HEADLINE and fire a group_headline_missing event so
 * we can find tenants that haven't set the key.
 */
export function GroupHero({ logoImage, groupSlug }: GroupHeroProps) {
  const { t } = useTranslation('booking');
  const { trackEvent } = useAnalytics();

  const rawHeadline = t('SELECT_RESTAURANT_SCREEN_TEXT_1');

  // "Blank" means: empty string, or the key itself (what a pass-through mock
  // returns), so both production misconfiguration and test stubs are caught.
  const headlineIsBlank =
    !rawHeadline || rawHeadline === 'SELECT_RESTAURANT_SCREEN_TEXT_1';

  const headline = headlineIsBlank ? t('GROUP_HERO_FALLBACK_HEADLINE') : rawHeadline;

  useEffect(() => {
    if (headlineIsBlank) {
      trackEvent({
        name: 'group_headline_missing',
        properties: { group_slug: groupSlug },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headlineIsBlank, groupSlug]);

  return (
    <header
      data-testid="group-hero"
      data-verify-unit="GroupHero"
      data-verify-has-logo={String(!!logoImage)}
      data-verify-headline-blank={String(headlineIsBlank)}
      className="w-full bg-background flex flex-col items-center gap-6 px-6 pt-10 pb-8"
    >
      {/* Language switcher — top-right via self-end */}
      <div className="self-end">
        <LanguageSelector size="sm" />
      </div>

      {/* Logo */}
      {logoImage && (
        <img
          src={logoImage}
          alt={t('GROUP_HERO_LOGO_ALT')}
          className="h-16 max-w-[220px] object-contain"
        />
      )}

      {/* Serif headline */}
      <h1 className="text-3xl sm:text-4xl font-display tracking-wide text-center text-foreground max-w-lg">
        {headline}
      </h1>
    </header>
  );
}

export default GroupHero;
